import { dispatchLayoutChange, tryExecution } from '../_util';
import Modal from 'bootstrap/js/src/modal';
import { enableBlurBackdrop } from '../plugins/bootstrap';

// -------------------------------------------------------------------------------------------------
// Constants
// -------------------------------------------------------------------------------------------------

const baseClass = 'modal';
const linkClass = 'modal__overlay-link';
const templateClass = 'modal__overlay-template';
const closeModalClass = 'close-modal';
const blurClass = 'modal--blur';
const closeButtonClass = 'modal-close-button';
const closeButtonRotateClass = 'modal-close-button--rotate';
const dialogClass = 'modal-dialog';
const dialogCustomClass = 'modal-dialog--custom';
const fullscreenClass = 'modal-fullscreen';
const templateIdAttr = 'data-template-id';
const linkTemplateIdAttr = 'data-link-template-id';

const EVENT_MODAL_OPEN = 'modal-overlay-open';

class ModalOverlay {
  /**
   * @private
   * @type {HTMLElement}
   */
  _el;

  /**
   * @private
   * @type {boolean}
   */
  _isOpen = false;

  /**
   * @private
   * @type {HTMLButtonElement|null}
   */
  _closeButton = null;

  /**
   * @private
   * @type {HTMLTemplateElement|null}
   */
  _overlayTemplate;

  /**
   * @private
   * @type {HTMLElement|null}
   */
  _overlay = null;

  /**
   * @type {Modal|null}
   * @private
   */
  _overlayModal = null;

  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this._el = element;
    
    // Find the associated template for this link
    this._overlayTemplate = this.findAssociatedTemplate();
    
    if (!this._overlayTemplate) {
      console.warn('Could not find overlay template for element:', this._el);
      return;
    }

    this.attachEvents();
  }

  /**
   * Find the template associated with this link.
   * Uses multiple strategies to ensure reliability with multiple links on the page.
   * 
   * @returns {HTMLElement|null}
   */
  findAssociatedTemplate() {
    // Strategy 1: Use data attributes if available
    const linkTemplateId = this._el.getAttribute(linkTemplateIdAttr);
    if (linkTemplateId) {
      const template = document.querySelector(`.${templateClass}[${templateIdAttr}="${linkTemplateId}"]`);
      if (template) return template;
    }
    
    // Strategy 2: Look for immediate next sibling
    if (this._el.nextElementSibling?.classList.contains(templateClass)) {
      return this._el.nextElementSibling;
    }
    
    // Strategy 3: Look for template within the same parent container
    let closestContainer = this._el.closest('li, div, section, article');
    if (closestContainer) {
      const containerTemplate = closestContainer.querySelector(`.${templateClass}`);
      if (containerTemplate) return containerTemplate;
    }
    
    // Strategy 4: Look for template within the same parent container
    closestContainer = this._el.parentElement;
    if (closestContainer) {
      // Use Array.from to get all templates in the parent and find the one closest to this link
      const templates = Array.from(closestContainer.querySelectorAll(`.${templateClass}`));
      if (templates.length > 0) {
        // Find position of this element among siblings
        const siblings = Array.from(closestContainer.children);
        const linkIndex = siblings.indexOf(this._el);
        
        if (linkIndex !== -1) {
          // Find the template that comes after this link
          for (let i = 0; i < templates.length; i++) {
            const templateIndex = siblings.indexOf(templates[i]);
            if (templateIndex > linkIndex) {
              return templates[i];
            }
          }
        }
        
        // If no template is found after the link, use the first template
        // (this is a fallback that might not be correct but is better than nothing)
        return templates[0];
      }
    }
    
    return null;
  }

  /**
   * Attaches click events to the modal link.
   */
  attachEvents() {
    this._el.addEventListener('click', (event) => {
      event.preventDefault();
      this.isOpen = true;
    });

    document.addEventListener(EVENT_MODAL_OPEN, () => this.isOpen = false);
  }

  /**
   * Attaches special events to close the modal.
   */
  attachCloseEvents() {
    this._closeButton?.addEventListener('click', this.handleCloseClick.bind(this), {
      passive: true,
    });

    this._overlay?.addEventListener('hidden.bs.modal', this.handleModalClose.bind(this), {
      passive: true,
    });

    // Handle elements with close-modal class
    const closeModalLinks = this._overlay.querySelectorAll(`.${closeModalClass}`);
    const self = this;
    for (let link of closeModalLinks) {
      link.addEventListener('click', function(event) {
        event.preventDefault();
        self._closeButton.click();
      });
    }
  }

  /**
   * Handles a click on a close button.
   */
  handleCloseClick() {
    this._overlayModal?.hide();
    this._closeButton?.removeEventListener('click', this.handleCloseClick.bind(this));
  }

  /**
   * Handles closing a modal after it has been closed completely.
   */
  handleModalClose() {
    this._overlay?.removeEventListener('hidden.bs.modal', this.handleModalClose.bind(this));
    this.isOpen = false;
  }

  /**
   * Get the content that should be visible inside the overlay.
   *
   * @returns {HTMLElement|null}
   */
  getOverlayTemplate() {
    if (!this._overlayTemplate) {
      return null;
    }

    return /** @type {HTMLElement} **/ this._overlayTemplate.content.cloneNode(true);
  }

  /**
   * Shows the modal overlay
   */
  showOverlay() {
    if (this._overlay) {
      return;
    }

    const template = this.getOverlayTemplate();
    if (!template) {
      console.warn('Could not find overlay template. Element: ', this._el);
      return;
    }

    document.dispatchEvent(new CustomEvent(EVENT_MODAL_OPEN));
    
    // Create and show the modal
    this.showModal(template);
    
    dispatchLayoutChange();
  }

  /**
   * Creates and shows the modal.
   *
   * @param {HTMLElement} template
   */
  showModal(template) {
    ({
      /** @type {HTMLElement} */ overlay: this._overlay,
      /** @type {HTMLElement} */ closeButton: this._closeButton
    } = this.createModalOverlay(template));

    document.body.append(this._overlay);

    this._overlayModal = /** @type {Modal} */ Modal.getOrCreateInstance(this._overlay, {
      backdrop: 'fix', // Any value other than true or 'static' would hide the modal on click or show ugly animation.
    });
    this._overlayModal.show();

    enableBlurBackdrop();
    this.attachCloseEvents();
  }

  /**
   * Hides any currently open overlay.
   */
  hideOverlay() {
    if (!this._overlay) {
      return;
    }
    
    this._overlayModal.hide();
    this._overlay.remove();

    this._overlayModal = null;
    this._overlay = null;
  }

  /**
   * Builds the markup for the modal overlay.
   *
   * @param {HTMLElement} innerElement
   * @returns {{ overlay: HTMLElement, closeButton: HTMLElement }}
   */
  createModalOverlay(innerElement) {
    const wrapper = document.createElement('div');
    wrapper.classList.add(...[baseClass, blurClass]);
    wrapper.tabIndex = -1;

    const closeButton = document.createElement('div');
    closeButton.classList.add(...[closeButtonClass, closeButtonRotateClass, 'ms-auto', 'd-flex', 'justify-content-end']);
    closeButton.innerHTML = `
        <button type="button">
            <svg viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg"><circle cx="17.5" cy="17.5" fill="none" r="17.5"></circle><g><path d="m17.5 35a17.5 17.5 0 1 1 17.5-17.5 17.52 17.52 0 0 1 -17.5 17.5zm0-34a16.5 16.5 0 1 0 16.5 16.5 16.52 16.52 0 0 0 -16.5-16.5z"></path><path d="m27.5 17h-9.5v-9.5h-1v9.5h-9.5v1h9.5v9.5h1v-9.5h9.5z"></path></g></svg>
        </button>
    `;

    const modalDialog = document.createElement('div');
    modalDialog.classList.add(...[dialogClass, fullscreenClass, dialogCustomClass]);
    modalDialog.append(innerElement);

    wrapper.append(closeButton);
    wrapper.append(modalDialog);

    return {
      overlay: wrapper,
      closeButton: closeButton,
    };
  }

  /**
   * @return {boolean}
   */
  get isOpen() {
    return this._isOpen;
  }

  /**
   * @param {boolean} value
   */
  set isOpen(value) {
    if (this._isOpen === value) {
      return;
    }

    if (value === false) {
      this.hideOverlay();
      this._isOpen = false;
      return;
    }

    this.showOverlay();
    this._isOpen = true;
  }
}

/**
 * Initialize modal overlay functionality for all modal__overlay-link elements
 * Also adds unique IDs to links and templates if they don't already have them
 */
export default function initializeModalOverlays() {
  // First, check if we have links without corresponding IDs
  setupTemplateIds();
  
  // Then initialize all modal links
  const modalLinks = document.querySelectorAll(`.${linkClass}`);
  for (const link of modalLinks) {
    tryExecution(() => new ModalOverlay(link));
  }
}

/**
 * Sets up template IDs for links and templates that don't have them
 */
function setupTemplateIds() {
  const modalLinks = document.querySelectorAll(`.${linkClass}`);
  
  modalLinks.forEach((link, index) => {
    // Skip if the link already has a template ID
    if (link.hasAttribute(linkTemplateIdAttr)) return;
    
    // Find the template that would be associated with this link
    let template = null;
    
    // Check immediate next sibling
    if (link.nextElementSibling?.classList.contains(templateClass)) {
      template = link.nextElementSibling;
    } else {
      // Try to find in parent container
      const container = link.closest('li, div, section, article');
      if (container) {
        template = container.querySelector(`.${templateClass}`);
      }
    }
    
    // If we found a template, set up the IDs
    if (template && !template.hasAttribute(templateIdAttr)) {
      const uniqueId = `modal-template-${index}`;
      link.setAttribute(linkTemplateIdAttr, uniqueId);
      template.setAttribute(templateIdAttr, uniqueId);
    }
  });
} 